import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import client from '../api/client';

const SubscriptionContext = createContext(null);

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({ children }) => {
  const { user, token, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!user || !token) {
      setSubscription(null);
      if (!authLoading) setLoading(false);
      return;
    }
    try {
      const res = await client.get('/subscriptions/me');
      setSubscription(res.data);
    } catch {
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, token?.slice?.(0, 20), authLoading]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const value = useMemo(() => {
    const hasFeature = (feature) => {
      if (!subscription) return false;
      const tier = subscription.tier || 'free';
      if (tier === 'coach' || tier === 'pro') return true;
      if (subscription.is_permanent) return true;
      const proFeatures = ['supplements', 'diet', 'unlimited_routines', 'unlimited_checkins', 'unlimited_exercises'];
      return !proFeatures.includes(feature);
    };

    const isProUser = () => {
      if (!subscription) return false;
      if (subscription.is_permanent) return true;
      if (!['active', 'grace', 'trialing'].includes(subscription.status)) return false;
      return ['pro', 'coach'].includes(subscription.tier);
    };

    const isCoach = () => {
      if (!subscription) return false;
      return subscription.tier === 'coach';
    };

    const isFreeUser = () => {
      if (!subscription) return true;
      return subscription.tier === 'free' && !subscription.is_permanent;
    };

    const isInGracePeriod = () => {
      return subscription?.status === 'grace';
    };

    const graceDaysRemaining = () => {
      if (!subscription?.subscription?.grace_period_end) return 0;
      const end = new Date(subscription.subscription.grace_period_end);
      const now = new Date();
      const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
      return Math.max(0, diff);
    };

    return {
      subscription,
      loading,
      hasFeature,
      isProUser,
      isCoach,
      isFreeUser,
      isInGracePeriod,
      graceDaysRemaining,
      refreshSubscription: fetchSubscription,
    };
  }, [subscription, loading, fetchSubscription]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
